import { FC, memo, useContext, useEffect, useMemo, useState } from "react";
import Section from "components/gonzo/parts/Section";
import AnchorTarget from "components/official/AnchorTarget";
import styled from "styled-components";
import { LibraryContext } from "lib/context/gonzo/LibraryContext";
import TwitterButton from "components/gonzo/parts/TwitterButton";
import Headline from "components/gonzo/parts/Headline";
import { SwiperClass } from "interfaces/gonzo/Swiper";
import Content from "components/gonzo/parts/Content";
import useGonzoImagePath from "lib/hooks/gonzo/useImagePath";
import BreakPc from "components/gonzo/parts/BreakPc";

const Campaign: FC = () => {
  const { imagePath: image } = useGonzoImagePath("campaign/");
  const [now, setNow] = useState<Date>();
  const campaignImageList = useMemo(() => CAMPAIGNS.map((campaign) => image(`image/${campaign.image}`)), [image]);
  const titleImageList = useMemo(
    () => CAMPAIGNS.map((campaign) => image(`select/${campaign.title}${now && campaign.endedAt < now ? "_end" : ""}.png`)),
    [image, now]
  );
  const firstIndex = useMemo(() => CAMPAIGNS.findIndex((campaign) => now && campaign.endedAt > now), [now]);
  const { slideTo, activeIndex } = useSwiper(firstIndex);
  const headlineImages = useMemo<[string, string]>(() => [image("camp_title_1.png"), image("camp_title_2.png")], [image]);
  useEffect(() => {
    setNow(new Date())
  }, [])
  return (
    <Section className="campaignSection">
      <AnchorTarget id="campaign" />
      <Headline src={headlineImages} alt="キャンペーン" />
      <Content>
        <EntryTitle>応募方法</EntryTitle>
        <EntryManual>
          <ManualStep>
            <StepIcon src={image("camp_oubo_01.png")} />
            <ManualText>
              <TwitterButton href="https://twitter.com/intent/follow?screen_name=GONZO30th" target="_blank">
                @GONZO30th<span style={{ fontSize: "0.7em" }}>をフォロー</span>
              </TwitterButton>
            </ManualText>
          </ManualStep>
          <ManualStep>
            <StepIcon src={image("camp_oubo_02.png")} />
            <ManualText>
              キャンペーン開催中の作品の
              <BreakPc />
              応募ボタンを押してツイートを表示
            </ManualText>
          </ManualStep>
          <ManualStep>
            <StepIcon src={image("camp_oubo_03.png")} />
            <ManualText>
              応募したい作品の
              <BreakPc />
              キャンペーンツイートをリツイート
            </ManualText>
          </ManualStep>
        </EntryManual>
        <AttentionText>
          <AttentionTitle>【注意事項】</AttentionTitle>
          <AttentionList>
            <AttentionItem>当選発表は当選者の方へのDMをもってかえさせていただきます。</AttentionItem>
            <AttentionItem>いただいた個人情報は、本キャンペーンのプレゼント発送のみに使用させていただきますのでご安心ください。</AttentionItem>
            <AttentionItem>発送時期が遅くなる可能性がございます。</AttentionItem>
            <AttentionItem>キャンペーン内容は予告なく変更される場合があります。</AttentionItem>
          </AttentionList>
        </AttentionText>
        <ImageSwiperArea>
          <SwiperButton className="swiper-button-prev" src={image("camp_left.png")} alt="前のキャンペーン画像へ" />
          <SwiperContainer className="campaign swiper">
            <SwiperWrapper className="swiper-wrapper">
              {campaignImageList.map((campaignImage, index) => (
                <SwiperSlide className="swiper-slide" key={index}>
                  <ImageContainer>
                    <SlideImage src={campaignImage} />
                    {CAMPAIGNS[index].tweetId && (
                      <TwitterEntryButton href={`https://twitter.com/intent/retweet?tweet_id=${CAMPAIGNS[index].tweetId}`} target="_blank">
                        応募する
                      </TwitterEntryButton>
                    )}
                  </ImageContainer>
                </SwiperSlide>
              ))}
            </SwiperWrapper>
          </SwiperContainer>
          <SwiperButton className="swiper-button-next" src={image("camp_right.png")} alt="次のキャンペーン画像へ" />
        </ImageSwiperArea>
        <TitleList>
          {titleImageList.map((imagePath, index) => (
            <TitleItem key={index} onClick={() => slideTo(index)} $active={activeIndex === index}>
              <TitleItemImage src={imagePath} alt="アニメタイトル" />
            </TitleItem>
          ))}
        </TitleList>
      </Content>
    </Section>
  );
};
export default memo(Campaign);

const CAMPAIGNS = [
  { title: "camp_select_07", image: "camp_image_01.jpg", endedAt: new Date(2022, 7, 13, 20, 0, 0), tweetId: "1556233702547628032" },
  { title: "camp_select_01", image: "camp_image_02.jpg", endedAt: new Date(2022, 7, 13, 20, 0, 0), tweetId: "1556233694079111168" },
  { title: "camp_select_02", image: "camp_image_03.jpg", endedAt: new Date(2022, 7, 20, 20, 0, 0), tweetId: "1558770407905415173" },
  { title: "camp_select_08", image: "camp_image_04.jpg", endedAt: new Date(2022, 7, 20, 20, 0, 0), tweetId: "1558770416193249280" },
  { title: "camp_select_06", image: "camp_image_05.jpg", endedAt: new Date(2022, 7, 27, 20, 0, 0), tweetId: "1561307131818565632" },
  { title: "camp_select_05", image: "camp_image_06.jpg", endedAt: new Date(2022, 7, 27, 20, 0, 0), tweetId: "1561307126932422657" },
  { title: "camp_select_11", image: "camp_image_07.jpg", endedAt: new Date(2022, 8, 3, 20, 0, 0), tweetId: "1563843837163778048" },
  { title: "camp_select_03", image: "camp_image_08.jpg", endedAt: new Date(2022, 8, 3, 20, 0, 0), tweetId: "1563843847125360640" },
  { title: "camp_select_12", image: "camp_image_09.jpg", endedAt: new Date(2022, 8, 10, 20, 0, 0), tweetId: "1566380564071952385" },
  { title: "camp_select_04", image: "camp_image_10.jpg", endedAt: new Date(2022, 8, 10, 20, 0, 0), tweetId: "1566380559181414403" },
  { title: "camp_select_13", image: "camp_image_11.jpg", endedAt: new Date(2022, 8, 17, 20, 0, 0), tweetId: "1568917276211945472" },
  { title: "camp_select_14", image: "camp_image_12.jpg", endedAt: new Date(2022, 8, 17, 20, 0, 0), tweetId: "1568917282130079744" },
  { title: "camp_select_10", image: "camp_image_13.jpg", endedAt: new Date(2022, 8, 24, 20, 0, 0), tweetId: "1571454005728845824" },
  { title: "camp_select_09", image: "camp_image_14.jpg", endedAt: new Date(2022, 8, 24, 20, 0, 0), tweetId: "1571454024481525761" },
];

const useSwiper = (firstIndex: number) => {
  const defaultIndex = firstIndex != -1 ? firstIndex : 0
  const { swiper: swiperReady } = useContext(LibraryContext);
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const slideTo = (index: number) => swiper?.slideTo(index);
  useEffect(() => {
    if (swiperReady) {
      const swiper = new window.Swiper(".campaign.swiper", {
        initialSlide: defaultIndex,
        navigation: {
          prevEl: ".campaignSection .swiper-button-prev",
          nextEl: ".campaignSection .swiper-button-next",
        },
      });
      setSwiper(swiper);
      setActiveIndex(swiper.activeIndex);
      swiper.on("activeIndexChange", (swiper) => {
        setActiveIndex(swiper.activeIndex);
      });
    }
  }, [swiperReady, firstIndex]);
  return { slideTo, activeIndex };
};

const EntryTitle = styled.h5`
  text-align: center;
  color: #f5dc4c;
  border: solid 1px #f5dc4c;
  padding: 10px;
  font-size: 1.3em;
  margin-bottom: 2em;
`;
const EntryManual = styled.ol`
  display: flex;
  align-items: center;
  margin-bottom: 2em;
  @media screen and (max-width: 960px) {
    flex-direction: column;
    justify-content: center;
  }
  @media screen and (min-width: 961px) {
    justify-content: space-around;
  }
`;
const ManualStep = styled.li`
  width: 100%;
  display: flex;
  align-items: center;
  @media screen and (max-width: 960px) {
    padding: 10px;
  }
  @media screen and (min-width: 961px) {
    padding: 20px;
  }
`;
const StepIcon = styled.img`
  width: 60px;
  height: 60px;
  @media screen and (max-width: 960px) {
    margin-right: 20px;
  }
  @media screen and (min-width: 961px) {
    margin-right: 30px;
  }
`;
const ManualText = styled.p`
  line-height: 1.62;
  color: white;

  & a {
    color: #1d9ff1;
  }
`;

const ImageSwiperArea = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SwiperContainer = styled.div`
  @media screen and (max-width: 960px) {
    flex-basis: 100%;
    width: 100%;
    margin-bottom: 30px;
  }
  @media screen and (min-width: 961px) {
    flex-basis: 70%;
    width: 70%;
    margin-bottom: 50px;
    &&.swiper {
      margin-left: 2%;
      margin-right: 2%;
    }
  }
`;
const SwiperWrapper = styled.div``;
const SwiperSlide = styled.div``;
const ImageContainer = styled.div`
  position: relative;
`;
const SlideImage = styled.img`
  width: 100%;
`;
const SwiperButton = styled.img`
  && {
    color: white;
    z-index: 10;

    &.swiper-button-prev {
      filter: drop-shadow(0 0 2px black);
      width: 26px;
      height: 26px;
      @media screen and (max-width: 960px) {
        position: absolute;
        left: 10px;
        z-index: 2;
      }
      @media screen and (min-width: 961px) {
        position: static;
      }
    }

    &.swiper-button-next {
      filter: drop-shadow(0 0 2px black);
      width: 26px;
      height: 26px;
      @media screen and (max-width: 960px) {
        position: absolute;
        right: 10px;
        z-index: 2;
      }
      @media screen and (min-width: 961px) {
        position: static;
      }
    }
  }
`;

const TitleList = styled.ul`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  @media screen and (max-width: 960px) {
    margin: -5px -5px 40px -5px;
  }
  @media screen and (min-width: 961px) {
    margin: -10px -10px 40px -10px;
  }
`;

const TitleItem = styled.li<{ $active: boolean }>`
  background-color: ${({ $active }) => ($active ? "#BFA510" : "#000000")};
  border: solid 1px white;
  @media screen and (max-width: 960px) {
    margin: 5px;
    flex-basis: calc(100% / 3 - 10px);
  }
  @media screen and (min-width: 961px) {
    margin: 10px;
    flex-basis: calc(100% / 6 - 20px);
  }
`;
const TitleItemImage = styled.img`
  color: white;
  width: 100%;
`;

const AttentionText = styled.div`
  margin: 50px auto;
  max-width: 700px;
  font-size: 0.8em;
  @media screen and (max-width: 960px) {
    font-size: 0.7em;
    margin: 30px auto;
  }
  line-height: 1.6em;
  color: #cccccc;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 20px;
`;

const AttentionTitle = styled.div`
  text-align: center;
  font-size: 16px;
  margin-bottom: 5px;
`;

const AttentionList = styled.ul``;
const AttentionItem = styled.li`
  margin-left: 10px;
  list-style: disc outside;
`;

const TwitterEntryButton = styled(TwitterButton)`
  position: absolute;
  display: block;
  text-align: center;
  @media screen and (max-width: 960px) {
    font-size: 3vw;
    width: 30%;
    height: 7vw;
    right: 10.2%;
    bottom: 16%;
  }
  @media screen and (min-width: 961px) {
    font-size: 1.4em;
    width: 29.3%;
    right: 10.5%;
    bottom: 16%;
  }
  @media screen and (min-width: 1281px) {
    font-size: 1.6em;
  }
  @media screen and (min-width: 1921px) {
    font-size: 1.8em;
  }
`;
